<template>
    <section class="review">
        <div v-if="isRestaurantLogoVisible && logo"
             class="review__logo">
            <img :src="logo" alt="">
        </div>

        <!-- FORM -->
        <template v-if="!reviewSent">
            <div class="review__intro">
                <h2 class="review__q">{{ $t('pages.review.title') }}</h2>
                <p class="review__sub">{{ $t('pages.review.message') }}</p>
            </div>

            <div class="review__stars-card">
                <div class="review__stars">
                    <button v-for="n in 5"
                            :key="n"
                            type="button"
                            class="review__star"
                            :class="{ 'is-lit': activeRating >= n }"
                            :aria-label="`${n}`"
                            @click="setRating(n)">
                        <svg viewBox="0 0 24 24" width="42" height="42" fill="none">
                            <path d="M12 17.27l5.18 3.29c.66.42 1.47-.2 1.3-.97l-1.38-5.9 4.58-3.97c.6-.52.28-1.5-.5-1.57l-6.03-.51L12.7 1.94c-.3-.72-1.32-.72-1.62 0L8.72 7.64l-6.03.51c-.78.07-1.1 1.05-.5 1.57l4.58 3.97-1.38 5.9c-.17.77.64 1.39 1.3.97L12 17.27Z"
                                  stroke-width="1.2" stroke-linejoin="round" />
                        </svg>
                    </button>
                </div>
                <div class="review__mood">{{ moodEmoji }}</div>
            </div>

            <transition name="review-fade">
                <div v-if="activeRating > 0" class="review__block">
                    <div class="review__label">{{ $t('pages.review.tagsLabel') }}</div>
                    <div class="review__tags">
                        <button v-for="tag in tagKeys"
                                :key="tag"
                                type="button"
                                class="review__tag"
                                :class="{ 'is-on': !!selectedTags[tag] }"
                                @click="toggleTag(tag)">
                            {{ $t(`pages.review.tags.${tag}`) }}
                        </button>
                    </div>
                </div>
            </transition>

            <div class="review__block">
                <div class="review__label">{{ $t('pages.review.comment') }}</div>
                <textarea v-model="message"
                          rows="4"
                          class="review__textarea"></textarea>
            </div>

            <div v-if="!hasStoredPhone" class="review__block">
                <div class="review__label">{{ $t('pages.review.phone') }}</div>
                <VuePhoneNumberInput v-model="clientPhone"
                                     default-country-code="PL"
                                     @update="updatePhone"
                                     :no-example="true"
                                     :preferred-countries="['PL', 'DE', 'ES', 'FR', 'UA', 'US']"
                                     :translations="getPhoneInputTranslations()"
                                     :border-radius="12"
                                     :error="phoneError" />
            </div>

            <VButton variant="primary"
                     gradient
                     size="lg"
                     full-width
                     class="review__submit"
                     :disabled="activeRating === 0"
                     v-on:click.native="sendReview()">
                {{ $t('pages.review.submit') }}
            </VButton>
        </template>

        <!-- THANK YOU -->
        <div v-else class="review__thanks">
            <div class="review__thanks-icon">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </div>
            <div class="review__thanks-text">
                <div class="review__thanks-title">{{ $t('pages.reviewLow.title') }}</div>
                <p class="review__thanks-sub">{{ $t('pages.reviewLow.message') }}</p>
            </div>

            <!-- Tipping nudge — a good review is the moment a guest is most
                 likely to tip, so offer it right after the thank-you. -->
            <div v-if="canTip" class="review__tip">
                <p class="review__tip-hint">{{ $t('pages.review.tipHint') }}</p>
                <VButton variant="primary"
                         gradient
                         size="lg"
                         full-width
                         v-on:click.native="openTips">
                    {{ $t('pages.review.tipCta') }}
                </VButton>
            </div>

            <div v-if="activeRating >= 4 && hasSocialLinks" class="review__social">
                <div class="review__label">{{ $t('pages.review.also') }}</div>
                <a v-if="url.tripadvisor" :href="url.tripadvisor" class="review__social-link" target="_blank" rel="noopener" @click.prevent="openSocial(url.tripadvisor)">
                    <img src="/img/socials/tripadvisor.jpg" alt="TripAdvisor">
                </a>
                <a v-if="url.google_url" :href="url.google_url" class="review__social-link" target="_blank" rel="noopener" @click.prevent="openSocial(url.google_url)">
                    <img src="/img/socials/google.jpg" alt="Google">
                </a>
                <a v-if="url.facebook_url" :href="url.facebook_url" class="review__social-link" target="_blank" rel="noopener" @click.prevent="openSocial(url.facebook_url)">
                    <img src="/img/socials/facebook.png" alt="Facebook">
                </a>
                <a v-if="url.yelp_url" :href="url.yelp_url" class="review__social-link" target="_blank" rel="noopener" @click.prevent="openSocial(url.yelp_url)">
                    <img src="/img/socials/yelp.jpg" alt="Yelp">
                </a>
                <a v-if="url.zomato_url" :href="url.zomato_url" class="review__social-link" target="_blank" rel="noopener" @click.prevent="openSocial(url.zomato_url)">
                    <img src="/img/socials/zomato.jpg" alt="Zomato">
                </a>
            </div>
        </div>

        <!-- "Review copied" bottom sheet (teleported to body) -->
        <div ref="copySheet" v-show="copyOpen" class="review-portal">
            <div class="review-portal__backdrop" :class="{ 'is-in': copyIn }" @click="closeCopySheet"></div>
            <div class="review-portal__sheet" :class="{ 'is-in': copyIn }">
                <div class="review-portal__handle"></div>
                <div class="review-portal__icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                        <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" stroke-width="1.8" />
                        <path d="M5 15V5a2 2 0 012-2h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    </svg>
                </div>
                <h3 class="review-portal__title">{{ $t('pages.review.copyTitle') }}</h3>
                <p class="review-portal__text">{{ $t('pages.review.copyText') }}</p>
                <VButton variant="primary" gradient size="lg" full-width v-on:click.native="confirmCopySheet">
                    {{ $t('pages.review.copyGotIt') }}
                </VButton>
            </div>
        </div>
    </section>
</template>

<script>
import VButton from "@/components/ui/VButton.vue";
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { BASE_API_URL } from "@/appConstants";
import axios from "axios";
import router from "@/router/router";
import { lockNav, unlockNav } from "@/utils/navLock";

const MOODS = ["", "😞", "😕", "🙂", "😊", "🤩"];

export default {
    name: "VReviewAdd",
    components: {
        VButton,
        VuePhoneNumberInput
    },
    props: {
        isRestaurantLogoVisible: { type: Boolean, default: true }
    },
    data() {
        return {
            activeRating: 0,
            message: "",
            reviewSent: false,
            copyOpen: false,
            copyIn: false,
            pendingUrl: null,
            selectedTags: {},
            tagKeys: ['food', 'service', 'atmosphere', 'speed', 'value'],
            clientPhone: '',
            formattedPhone: null,
            hasStoredPhone: false,
            phoneError: false,
            url: {
                tripadvisor: null,
                google_url: null,
                facebook_url: null,
                yelp_url: null,
                zomato_url: null,
            }
        };
    },
    mounted() {
        const storedPhone = window.localStorage.getItem('sm_clientPhone');
        if (storedPhone) {
            this.clientPhone = storedPhone;
            this.hasStoredPhone = true;
        }

        for (const key in this.url) {
            if (Object.prototype.hasOwnProperty.call(this.computedCompanyData.locale, key)) {
                this.url[key] = this.toHttpsUrl(this.computedCompanyData.locale[key]);
            }
        }

        // Teleport the copy sheet to <body> so its fixed positioning is correct.
        if (this.$refs.copySheet) {
            document.body.appendChild(this.$refs.copySheet);
        }
    },
    beforeDestroy() {
        if (this.copyOpen) unlockNav();
        if (this.$refs.copySheet && this.$refs.copySheet.parentNode) {
            this.$refs.copySheet.parentNode.removeChild(this.$refs.copySheet);
        }
    },
    computed: {
        computedCompanyData: function () {
            return this.$store.getters['getCompanyData'];
        },
        comTable() {
            return this.$store.getters['getTableData'];
        },
        logo() {
            return this.computedCompanyData.locale.logo;
        },
        moodEmoji() {
            return MOODS[this.activeRating] || '';
        },
        hasSocialLinks() {
            return Object.values(this.url).some(Boolean);
        },
        activeWaiters() {
            return (this.$store.getters['getWaitersList'] || []).filter(w => w.is_active === 1);
        },
        tipEnabled() {
            const navItems = this.computedCompanyData.locale.nav_items || {};
            return !!navItems.tip;
        },
        // 3★ and up reads as a satisfied guest — that's where a tip prompt is
        // welcome rather than tone-deaf.
        canTip() {
            return this.activeRating >= 3 && this.tipEnabled && this.activeWaiters.length > 0;
        }
    },
    methods: {
        setRating(val) {
            this.activeRating = val;
        },
        openTips() {
            router.push('/tip');
        },
        toggleTag(tag) {
            this.$set(this.selectedTags, tag, !this.selectedTags[tag]);
        },
        openSocial(targetUrl) {
            if (!targetUrl) return;
            const text = (this.message || '').trim();
            // With a written comment: copy it and prompt the guest to paste it on
            // the external site. Without one: go straight there.
            if (text) {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(text).catch(() => {});
                }
                this.pendingUrl = targetUrl;
                this.copyOpen = true;
                lockNav();
                this.$nextTick(() => {
                    window.requestAnimationFrame(() => { this.copyIn = true; });
                });
            } else {
                window.open(targetUrl, '_blank');
            }
        },
        closeCopySheet() {
            this.copyIn = false;
            unlockNav();
            setTimeout(() => { this.copyOpen = false; }, 280);
        },
        confirmCopySheet() {
            const target = this.pendingUrl;
            this.closeCopySheet();
            if (target) window.open(target, '_blank');
        },
        updatePhone(value) {
            this.formattedPhone = value;
            this.phoneError = false;
        },
        getPhoneInputTranslations() {
            return {
                countrySelectorLabel: this.$t('pages.cart.countrySelectorLabel'),
                countrySelectorError: this.$t('pages.cart.countrySelectorError'),
                phoneNumberLabel: this.$t('pages.cart.phoneNumberLabel')
            };
        },
        sendReview() {
            if (this.activeRating === 0) return; // submit is disabled until rated

            // Selected tags are folded into the review text (we have no separate
            // tags field on the backend) so they're actually submitted.
            const tagLabels = this.tagKeys
                .filter(t => this.selectedTags[t])
                .map(t => this.$t(`pages.review.tags.${t}`));
            const reviewText = [
                tagLabels.length ? `[${tagLabels.join(', ')}]` : '',
                this.message
            ].filter(Boolean).join(' ').trim();

            var endpoint = BASE_API_URL +
                'addreview?locale_id=' + this.$store.state.companyData.locale.id +
                '&mark=' + this.activeRating +
                '&review=' + encodeURIComponent(reviewText);

            if (this.comTable) {
                endpoint += '&table_id=' + this.comTable.id;
            }

            let phone = null;
            if (this.hasStoredPhone) {
                phone = this.clientPhone;
            } else if (this.clientPhone) {
                if (!this.formattedPhone || !this.formattedPhone.isValid) {
                    this.phoneError = true;
                    this.$toast.error(this.$t('pages.cart.invalidPhone'), { position: 'top-center' });
                    return;
                }
                phone = this.formattedPhone.formattedNumber;
            }
            if (phone) {
                endpoint += '&phone=' + encodeURIComponent(phone);
            }

            axios.get(endpoint).then(
                response => {
                    if (response.status === 200) {
                        this.$toast.success(this.$t('pages.reviewLow.title'));
                        this.reviewSent = true;
                    }
                }
            ).catch(error => {
                router.push('error');
                console.log(error);
            });
        },
        toHttpsUrl(input) {
            if (!/^https?:\/\//i.test(input)) {
                input = 'https://' + input;
            }

            try {
                const url = new URL(input);

                if (!/\.[a-z]{2,}$/i.test(url.hostname)) {
                    throw new Error('Invalid domain');
                }

                url.protocol = 'https:';
                return url.toString();
            } catch (err) {
                console.error('Invalid URL:', input, '|', err.message);
                return null;
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.review {
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 4px 0 8px;
}

.review__logo {
    display: flex;
    justify-content: center;

    img {
        max-width: 160px;
        max-height: 88px;
        object-fit: contain;
        padding: 12px;
        border-radius: var(--radius-3xl);
        background: var(--surface-card);
    }
}

.review__intro {
    .review__q {
        margin: 0;
        font-size: 21px;
        font-weight: var(--weight-bold);
        letter-spacing: -0.01em;
        color: var(--text-primary);
    }

    .review__sub {
        margin: 7px 0 0;
        font-size: 13.5px;
        font-weight: var(--weight-medium);
        line-height: 1.45;
        color: var(--text-secondary);
    }
}

/* ---- Star card ---- */
.review__stars-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;

    padding: 22px 16px;

    border: 1px solid var(--border-subtle);
    border-radius: 22px;
    background: var(--surface-card);
}

.review__stars {
    display: flex;
    gap: 8px;
}

.review__star {
    padding: 2px;
    border: none;
    background: transparent;
    cursor: pointer;
    line-height: 0;
    -webkit-tap-highlight-color: transparent;
    transition: transform var(--dur-fast) var(--ease-spring);

    svg path {
        fill: var(--surface-subtle);
        stroke: var(--border-strong);
        transition: fill var(--dur-base) var(--ease-standard),
                    stroke var(--dur-base) var(--ease-standard);
    }

    &.is-lit svg path {
        fill: #FFB23E;
        stroke: #F59412;
    }

    &:active {
        transform: scale(0.88);
    }
}

.review__mood {
    height: 24px;
    font-size: 22px;
    line-height: 1;
}

/* ---- Blocks (tags, comment) ---- */
.review__label {
    margin-bottom: 11px;

    font-size: 12px;
    font-weight: var(--weight-bold);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
}

.review__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
}

.review__tag {
    padding: 10px 16px;

    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: var(--weight-bold);

    border: 1.5px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-secondary);
    cursor: pointer;

    transition: all var(--dur-base) var(--ease-standard);

    &.is-on {
        border-color: var(--color-primary);
        background: var(--color-primary-soft);
        color: var(--color-primary);
    }
}

.review__textarea {
    box-sizing: border-box;
    width: 100%;
    resize: none;

    padding: 14px 16px;

    border: 1px solid var(--border-default);
    border-radius: 18px;
    background: var(--surface-card);

    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: var(--weight-medium);
    line-height: 1.45;
    color: var(--text-primary);

    transition: border-color var(--dur-base) var(--ease-standard),
                box-shadow var(--dur-base) var(--ease-standard);

    &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--focus-ring);
    }
}

.review__submit {
    margin-top: 2px;
}

/* ---- Thank you ---- */
.review__thanks {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;

    padding: 32px 8px 8px;
}

.review__thanks-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 92px;
    height: 92px;

    border-radius: 50%;
    background: var(--grad-brand);
    box-shadow: 0 14px 30px var(--sm-glow);

    animation: review-pop 0.42s var(--ease-spring) both;
}

@keyframes review-pop {
    from { transform: scale(0.5); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
}

.review__thanks-title {
    font-size: 23px;
    font-weight: var(--weight-bold);
    color: var(--text-primary);
}

.review__thanks-sub {
    margin: 8px auto 0;
    max-width: 280px;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--text-secondary);
}

/* ---- Tip nudge ---- */
.review__tip {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.review__tip-hint {
    margin: 0;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--text-secondary);
}

.review__social {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;

    .review__label {
        margin-bottom: 0;
        text-align: center;
    }
}

.review__social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-2xl);
    background: var(--surface-card);
    box-shadow: var(--shadow-sm);

    transition: transform var(--dur-fast) var(--ease-standard);

    &:active { transform: scale(0.98); }

    img {
        max-width: 100%;
        display: block;
        border-radius: var(--radius-lg);
    }
}

/* fade for the tags block */
.review-fade-enter-active { transition: opacity var(--dur-base) var(--ease-standard); }
.review-fade-enter { opacity: 0; }

/* ---- "Review copied" bottom sheet ---- */
.review-portal {
    position: fixed;
    inset: 0;
    z-index: 1000;
}

.review-portal__backdrop {
    position: absolute;
    inset: 0;
    background: var(--overlay-scrim);
    opacity: 0;
    transition: opacity 280ms var(--ease-standard);

    &.is-in { opacity: 1; }
}

.review-portal__sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    padding: 12px 22px calc(28px + var(--safe-bottom, 0px));

    background: var(--surface-card);
    border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
    box-shadow: var(--shadow-xl);

    transform: translateY(100%);
    transition: transform 320ms var(--ease-out);

    &.is-in { transform: translateY(0); }
}

.review-portal__handle {
    width: 42px;
    height: 5px;
    margin: 0 auto 16px;
    border-radius: var(--radius-pill);
    background: var(--border-default);
}

.review-portal__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 14px;

    border-radius: 16px;
    background: var(--color-primary-soft);
    color: var(--color-primary);
}

.review-portal__title {
    margin: 0;
    font-size: var(--text-xl);
    font-weight: var(--weight-black);
    color: var(--text-primary);
}

.review-portal__text {
    margin: 8px 0 18px;
    max-width: 320px;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--text-secondary);
}
</style>
